import { useMsal, useIsAuthenticated } from '@azure/msal-react'
import { useEffect, useState } from 'react'
import { loginRequest } from '@/authConfig'
import type { CurrentUser } from '@/types'
import { userApi } from '@/services/api'

export enum RoleLevel {
  CONTRIBUTOR = 1,
  PROJECT_MANAGER = 2,
  PROJECT_ADMIN = 3,
  ADMIN = 4,
  SUPER_ADMIN = 5,
}

const ROLE_HIERARCHY: Record<string, RoleLevel> = {
  'Contributor': RoleLevel.CONTRIBUTOR,
  'Project Manager': RoleLevel.PROJECT_MANAGER,
  'Project Administrator': RoleLevel.PROJECT_ADMIN,
  'Administrator': RoleLevel.ADMIN,
  'Super Administrator': RoleLevel.SUPER_ADMIN,
}

export function useAuth() {
  const { instance, accounts } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false)
      return
    }

    const fetchUser = async () => {
      try {
        const response = await userApi.getCurrentUser()
        setCurrentUser(response.data)
      } catch (error) {
        console.error('Failed to fetch current user:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchUser()
  }, [isAuthenticated])

  const hasRole = (role: string): boolean => {
    if (!currentUser) return false
    return currentUser.systemRoles.includes(role)
  }

  const hasMinRole = (minRole: string): boolean => {
    if (!currentUser) return false

    const userRoles = currentUser.systemRoles
    const minLevel = ROLE_HIERARCHY[minRole] || RoleLevel.CONTRIBUTOR

    for (const role of userRoles) {
      const level = ROLE_HIERARCHY[role] || RoleLevel.CONTRIBUTOR
      if (level >= minLevel) {
        return true
      }
    }

    return false
  }

  const login = async () => {
    try {
      await instance.loginPopup(loginRequest)
    } catch (error) {
      console.error('Login failed:', error)
    }
  }

  const logout = async () => {
    try {
      await instance.logoutRedirect()
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const getAccessToken = async (): Promise<string> => {
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      })
      return response.accessToken
    } catch (error) {
      console.error('Failed to acquire token:', error)
      throw error
    }
  }

  return {
    currentUser,
    isLoading,
    isAuthenticated,
    hasRole,
    hasMinRole,
    login,
    logout,
    getAccessToken,
    account: accounts[0],
  }
}
